import { useCallback, useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";

type TabId =
  | "fpa"
  | "wait-return"
  | "overnight"
  | "holidays"
  | "via"
  | "cash";

const TABS: { id: TabId; label: string; hint?: string }[] = [
  {
    id: "fpa",
    label: "Free Pickup Areas (FPA)",
    hint: "Pickup areas you can cover without charging to travel from your base to the pickup point.",
  },
  { id: "wait-return", label: "Wait and Return" },
  { id: "overnight", label: "Overnight Charges" },
  { id: "holidays", label: "Public Holiday Uplift" },
  { id: "via", label: "VIA Settings" },
  { id: "cash", label: "Cash Payment Radius" },
];

type FreePickupArea = {
  id: string;
  description: string | null;
  status: boolean;
  remarks: string | null;
  postcodes: { postCode: string }[];
};

type OvernightCharge = {
  id: string;
  fromTime: string | null;
  toTime: string | null;
  fromTimeString: string | null;
  toTimeString: string | null;
  priceAdjustPers: number | null;
  status: boolean;
  remarks: string | null;
};

type PublicHoliday = {
  id: string;
  name: string;
  holidayDate: string;
  upliftPercent: number;
  status: boolean;
  remarks: string | null;
};

function emptyFpa(): Omit<FreePickupArea, "id"> {
  return { description: "", status: true, remarks: "", postcodes: [{ postCode: "" }] };
}

export default function MorePricingOptions() {
  const [activeTab, setActiveTab] = useState<TabId>("fpa");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // FPA
  const [fpaList, setFpaList] = useState<FreePickupArea[]>([]);
  const [fpaForm, setFpaForm] = useState(emptyFpa());
  const [editingFpaId, setEditingFpaId] = useState<string | null>(null);
  const [baseSettings, setBaseSettings] = useState({
    basePostCode: "",
    baseLatitude: "",
    baseLongitude: "",
    deadLegChargePerMile: "",
  });

  // Overnight
  const [overnightList, setOvernightList] = useState<OvernightCharge[]>([]);
  const [overnightForm, setOvernightForm] = useState({
    fromTime: "22:00",
    toTime: "06:00",
    fromTimeString: "22:00",
    toTimeString: "06:00",
    priceAdjustPers: 15,
    status: true,
    remarks: "",
  });
  const [editingOvernightId, setEditingOvernightId] = useState<string | null>(null);

  // Holidays
  const [holidayList, setHolidayList] = useState<PublicHoliday[]>([]);
  const [holidayForm, setHolidayForm] = useState({
    name: "",
    holidayDate: "",
    upliftPercent: 25,
    status: true,
    remarks: "",
  });
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);

  // Singletons
  const [waitReturn, setWaitReturn] = useState({
    enabled: false,
    flatCharge: "",
    percentOfFare: "",
    minimumCharge: "",
    freeWaitingMinutes: "15",
    status: true,
    remarks: "",
  });
  const [viaSettings, setViaSettings] = useState({
    chargePerVia: "",
    freeVias: "0",
    maxVias: "",
    useLocationCharges: true,
    waitPricingEnabled: true,
    chargePerWaitStop: "",
    percentOfFarePerWaitStop: "",
    minimumWaitChargePerStop: "",
    freeWaitMinutesPerStop: "15",
    status: true,
    remarks: "",
  });
  const [cashSettings, setCashSettings] = useState({
    enabled: false,
    baseLatitude: "",
    baseLongitude: "",
    radiusMiles: "",
    status: true,
    remarks: "",
  });

  const loadFpa = useCallback(async () => {
    const [areas, company] = await Promise.all([
      axiosInstance.get("/free-pickup-areas"),
      axiosInstance.get("/company-settings"),
    ]);
    setFpaList(areas.data);
    const c = company.data;
    setBaseSettings({
      basePostCode: c.basePostCode ?? "",
      baseLatitude: c.baseLatitude != null ? String(c.baseLatitude) : "",
      baseLongitude: c.baseLongitude != null ? String(c.baseLongitude) : "",
      deadLegChargePerMile:
        c.deadLegChargePerMile != null ? String(c.deadLegChargePerMile) : "",
    });
  }, []);

  const loadOvernight = useCallback(async () => {
    const res = await axiosInstance.get("/overnight-charges");
    setOvernightList(res.data);
  }, []);

  const loadHolidays = useCallback(async () => {
    const res = await axiosInstance.get("/public-holidays");
    setHolidayList(
      res.data.map((h: PublicHoliday) => ({
        ...h,
        holidayDate: h.holidayDate?.slice?.(0, 10) ?? h.holidayDate,
      })),
    );
  }, []);

  const loadSingletons = useCallback(async () => {
    const [wait, via, cash] = await Promise.all([
      axiosInstance.get("/wait-return-settings"),
      axiosInstance.get("/via-settings"),
      axiosInstance.get("/cash-payment-settings"),
    ]);
    const w = wait.data;
    setWaitReturn({
      enabled: w.enabled ?? false,
      flatCharge: w.flatCharge != null ? String(w.flatCharge) : "",
      percentOfFare: w.percentOfFare != null ? String(w.percentOfFare) : "",
      minimumCharge: w.minimumCharge != null ? String(w.minimumCharge) : "",
      freeWaitingMinutes:
        w.freeWaitingMinutes != null ? String(w.freeWaitingMinutes) : "15",
      status: w.status ?? true,
      remarks: w.remarks ?? "",
    });
    const v = via.data;
    setViaSettings({
      chargePerVia: v.chargePerVia != null ? String(v.chargePerVia) : "",
      freeVias: String(v.freeVias ?? 0),
      maxVias: v.maxVias != null ? String(v.maxVias) : "",
      useLocationCharges: v.useLocationCharges ?? true,
      waitPricingEnabled: v.waitPricingEnabled ?? true,
      chargePerWaitStop: v.chargePerWaitStop != null ? String(v.chargePerWaitStop) : "",
      percentOfFarePerWaitStop:
        v.percentOfFarePerWaitStop != null ? String(v.percentOfFarePerWaitStop) : "",
      minimumWaitChargePerStop:
        v.minimumWaitChargePerStop != null ? String(v.minimumWaitChargePerStop) : "",
      freeWaitMinutesPerStop:
        v.freeWaitMinutesPerStop != null ? String(v.freeWaitMinutesPerStop) : "15",
      status: v.status ?? true,
      remarks: v.remarks ?? "",
    });
    const c = cash.data;
    setCashSettings({
      enabled: c.enabled ?? false,
      baseLatitude: c.baseLatitude != null ? String(c.baseLatitude) : "",
      baseLongitude: c.baseLongitude != null ? String(c.baseLongitude) : "",
      radiusMiles: c.radiusMiles != null ? String(c.radiusMiles) : "",
      status: c.status ?? true,
      remarks: c.remarks ?? "",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    setMessage("");
    const loaders: Record<TabId, () => Promise<void>> = {
      fpa: loadFpa,
      overnight: loadOvernight,
      holidays: loadHolidays,
      "wait-return": loadSingletons,
      via: loadSingletons,
      cash: loadSingletons,
    };
    loaders[activeTab]()
      .catch(() => setMessage("Failed to load settings"))
      .finally(() => setLoading(false));
  }, [activeTab, loadFpa, loadOvernight, loadHolidays, loadSingletons]);

  const saveBaseSettings = async () => {
    const current = (await axiosInstance.get("/company-settings")).data;
    await axiosInstance.put("/company-settings", {
      ...current,
      basePostCode: baseSettings.basePostCode || null,
      baseLatitude: baseSettings.baseLatitude ? Number(baseSettings.baseLatitude) : null,
      baseLongitude: baseSettings.baseLongitude ? Number(baseSettings.baseLongitude) : null,
      deadLegChargePerMile: baseSettings.deadLegChargePerMile
        ? Number(baseSettings.deadLegChargePerMile)
        : null,
    });
    setMessage("Base location saved");
  };

  const saveFpa = async () => {
    const payload = {
      description: fpaForm.description || null,
      status: fpaForm.status,
      remarks: fpaForm.remarks || null,
      postcodes: fpaForm.postcodes.filter((p) => p.postCode.trim()),
    };
    if (editingFpaId) {
      await axiosInstance.put(`/free-pickup-areas/${editingFpaId}`, payload);
    } else {
      await axiosInstance.post("/free-pickup-areas", payload);
    }
    setFpaForm(emptyFpa());
    setEditingFpaId(null);
    await loadFpa();
    setMessage("Free pickup area saved");
  };

  const saveOvernight = async () => {
    const payload = { ...overnightForm, priceAdjustPers: Number(overnightForm.priceAdjustPers) };
    if (editingOvernightId) {
      await axiosInstance.put(`/overnight-charges/${editingOvernightId}`, payload);
    } else {
      await axiosInstance.post("/overnight-charges", payload);
    }
    setEditingOvernightId(null);
    await loadOvernight();
    setMessage("Overnight charge saved");
  };

  const saveHoliday = async () => {
    const payload = {
      ...holidayForm,
      upliftPercent: Number(holidayForm.upliftPercent),
    };
    if (editingHolidayId) {
      await axiosInstance.put(`/public-holidays/${editingHolidayId}`, payload);
    } else {
      await axiosInstance.post("/public-holidays", payload);
    }
    setEditingHolidayId(null);
    await loadHolidays();
    setMessage("Public holiday saved");
  };

  const saveWaitReturn = async () => {
    await axiosInstance.put("/wait-return-settings", {
      enabled: waitReturn.enabled,
      flatCharge: waitReturn.flatCharge ? Number(waitReturn.flatCharge) : null,
      percentOfFare: waitReturn.percentOfFare ? Number(waitReturn.percentOfFare) : null,
      minimumCharge: waitReturn.minimumCharge ? Number(waitReturn.minimumCharge) : null,
      freeWaitingMinutes: waitReturn.freeWaitingMinutes
        ? Number(waitReturn.freeWaitingMinutes)
        : null,
      status: waitReturn.status,
      remarks: waitReturn.remarks || null,
    });
    setMessage("Wait and return settings saved");
  };

  const saveVia = async () => {
    await axiosInstance.put("/via-settings", {
      chargePerVia: viaSettings.chargePerVia ? Number(viaSettings.chargePerVia) : null,
      freeVias: Number(viaSettings.freeVias) || 0,
      maxVias: viaSettings.maxVias ? Number(viaSettings.maxVias) : null,
      useLocationCharges: viaSettings.useLocationCharges,
      waitPricingEnabled: viaSettings.waitPricingEnabled,
      chargePerWaitStop: viaSettings.chargePerWaitStop
        ? Number(viaSettings.chargePerWaitStop)
        : null,
      percentOfFarePerWaitStop: viaSettings.percentOfFarePerWaitStop
        ? Number(viaSettings.percentOfFarePerWaitStop)
        : null,
      minimumWaitChargePerStop: viaSettings.minimumWaitChargePerStop
        ? Number(viaSettings.minimumWaitChargePerStop)
        : null,
      freeWaitMinutesPerStop: viaSettings.freeWaitMinutesPerStop
        ? Number(viaSettings.freeWaitMinutesPerStop)
        : null,
      status: viaSettings.status,
      remarks: viaSettings.remarks || null,
    });
    setMessage("VIA settings saved");
  };

  const saveCash = async () => {
    await axiosInstance.put("/cash-payment-settings", {
      enabled: cashSettings.enabled,
      baseLatitude: cashSettings.baseLatitude ? Number(cashSettings.baseLatitude) : null,
      baseLongitude: cashSettings.baseLongitude ? Number(cashSettings.baseLongitude) : null,
      radiusMiles: cashSettings.radiusMiles ? Number(cashSettings.radiusMiles) : null,
      status: cashSettings.status,
      remarks: cashSettings.remarks || null,
    });
    setMessage("Cash payment settings saved");
  };

  const activeHint = TABS.find((t) => t.id === activeTab)?.hint;

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="More Pricing Options" />

      <div className="mt-6 border-b border-gray-200">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-brand-500 text-brand-600 dark:text-brand-400"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeHint && (
        <p className="mt-4 text-sm text-gray-600">{activeHint}</p>
      )}
      {message && <p className="mt-2 text-sm text-green-600">{message}</p>}
      {loading && <p className="mt-4 text-gray-500">Loading...</p>}

      {!loading && activeTab === "fpa" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-800">Company base (for dead-leg)</h3>
            <p className="text-sm text-gray-500">
              Pickups outside free areas are charged per mile from base to pickup.
            </p>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Base postcode"
              value={baseSettings.basePostCode}
              onChange={(e) =>
                setBaseSettings((s) => ({ ...s, basePostCode: e.target.value }))
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="Latitude"
                value={baseSettings.baseLatitude}
                onChange={(e) =>
                  setBaseSettings((s) => ({ ...s, baseLatitude: e.target.value }))
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="Longitude"
                value={baseSettings.baseLongitude}
                onChange={(e) =>
                  setBaseSettings((s) => ({ ...s, baseLongitude: e.target.value }))
                }
              />
            </div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Dead-leg charge per mile (£)"
              value={baseSettings.deadLegChargePerMile}
              onChange={(e) =>
                setBaseSettings((s) => ({ ...s, deadLegChargePerMile: e.target.value }))
              }
            />
            <button
              type="button"
              onClick={saveBaseSettings}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg"
            >
              Save base location
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-800">
              {editingFpaId ? "Edit" : "Add"} free pickup area
            </h3>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Description"
              value={fpaForm.description ?? ""}
              onChange={(e) => setFpaForm((f) => ({ ...f, description: e.target.value }))}
            />
            {fpaForm.postcodes.map((p, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="flex-1 border rounded-lg px-3 py-2"
                  placeholder="Postcode prefix e.g. SO14"
                  value={p.postCode}
                  onChange={(e) => {
                    const postcodes = [...fpaForm.postcodes];
                    postcodes[i] = { postCode: e.target.value };
                    setFpaForm((f) => ({ ...f, postcodes }));
                  }}
                />
                <button
                  type="button"
                  className="text-red-600 text-sm"
                  onClick={() =>
                    setFpaForm((f) => ({
                      ...f,
                      postcodes: f.postcodes.filter((_, j) => j !== i),
                    }))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-blue-600"
              onClick={() =>
                setFpaForm((f) => ({
                  ...f,
                  postcodes: [...f.postcodes, { postCode: "" }],
                }))
              }
            >
              + Add postcode
            </button>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={fpaForm.status}
                onChange={(e) => setFpaForm((f) => ({ ...f, status: e.target.checked }))}
              />
              Active
            </label>
            <button
              type="button"
              onClick={saveFpa}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg"
            >
              Save area
            </button>
          </div>

          <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Postcodes</th>
                  <th className="px-4 py-2 text-left">Active</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {fpaList.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2">{row.description}</td>
                    <td className="px-4 py-2">
                      {row.postcodes.map((p) => p.postCode).join(", ")}
                    </td>
                    <td className="px-4 py-2">{row.status ? "Yes" : "No"}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <div className="flex items-center justify-end gap-3">
                        <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => {
                          setEditingFpaId(row.id);
                          setFpaForm({
                            description: row.description ?? "",
                            status: row.status,
                            remarks: row.remarks ?? "",
                            postcodes: row.postcodes.length
                              ? row.postcodes
                              : [{ postCode: "" }],
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-600"
                        onClick={async () => {
                          await axiosInstance.delete(`/free-pickup-areas/${row.id}`);
                          await loadFpa();
                        }}
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === "wait-return" && (
        <form
          className="mt-6 max-w-lg bg-white rounded-lg shadow p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            saveWaitReturn();
          }}
        >
          <p className="text-sm text-gray-600">
            Pricing when the driver waits at the final destination and returns the passenger
            (classic wait &amp; return journey).
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={waitReturn.enabled}
              onChange={(e) =>
                setWaitReturn((s) => ({ ...s, enabled: e.target.checked }))
              }
            />
            Enable wait and return pricing
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Flat charge (£)"
            value={waitReturn.flatCharge}
            onChange={(e) => setWaitReturn((s) => ({ ...s, flatCharge: e.target.value }))}
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="% of fare"
            value={waitReturn.percentOfFare}
            onChange={(e) =>
              setWaitReturn((s) => ({ ...s, percentOfFare: e.target.value }))
            }
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Minimum charge (£)"
            value={waitReturn.minimumCharge}
            onChange={(e) =>
              setWaitReturn((s) => ({ ...s, minimumCharge: e.target.value }))
            }
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Free waiting minutes"
            value={waitReturn.freeWaitingMinutes}
            onChange={(e) =>
              setWaitReturn((s) => ({ ...s, freeWaitingMinutes: e.target.value }))
            }
          />
          <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg">
            Save
          </button>
        </form>
      )}

      {!loading && activeTab === "overnight" && (
        <div className="mt-6 space-y-6">
          <form
            className="max-w-lg bg-white rounded-lg shadow p-6 space-y-4 grid grid-cols-2 gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveOvernight();
            }}
          >
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="From (HH:mm)"
              value={overnightForm.fromTime}
              onChange={(e) =>
                setOvernightForm((f) => ({
                  ...f,
                  fromTime: e.target.value,
                  fromTimeString: e.target.value,
                }))
              }
            />
            <input
              className="border rounded-lg px-3 py-2"
              placeholder="To (HH:mm)"
              value={overnightForm.toTime}
              onChange={(e) =>
                setOvernightForm((f) => ({
                  ...f,
                  toTime: e.target.value,
                  toTimeString: e.target.value,
                }))
              }
            />
            <input
              className="col-span-2 border rounded-lg px-3 py-2"
              type="number"
              placeholder="Uplift %"
              value={overnightForm.priceAdjustPers}
              onChange={(e) =>
                setOvernightForm((f) => ({
                  ...f,
                  priceAdjustPers: Number(e.target.value),
                }))
              }
            />
            <button type="submit" className="col-span-2 px-4 py-2 bg-brand-500 text-white rounded-lg">
              {editingOvernightId ? "Update" : "Add"} overnight rule
            </button>
          </form>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-4 py-2 text-left">Window</th>
                  <th className="px-4 py-2 text-left">Uplift %</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {overnightList.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2">
                      {row.fromTimeString} – {row.toTimeString}
                    </td>
                    <td className="px-4 py-2">{row.priceAdjustPers}%</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <div className="flex items-center justify-end gap-3">
                        <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => {
                          setEditingOvernightId(row.id);
                          setOvernightForm({
                            fromTime: row.fromTime ?? "",
                            toTime: row.toTime ?? "",
                            fromTimeString: row.fromTimeString ?? "",
                            toTimeString: row.toTimeString ?? "",
                            priceAdjustPers: Number(row.priceAdjustPers ?? 0),
                            status: row.status,
                            remarks: row.remarks ?? "",
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-600"
                        onClick={async () => {
                          await axiosInstance.delete(`/overnight-charges/${row.id}`);
                          await loadOvernight();
                        }}
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === "holidays" && (
        <div className="mt-6 space-y-6">
          <form
            className="max-w-lg bg-white rounded-lg shadow p-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              saveHoliday();
            }}
          >
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Holiday name"
              value={holidayForm.name}
              onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <input
              className="w-full border rounded-lg px-3 py-2"
              type="date"
              value={holidayForm.holidayDate}
              onChange={(e) =>
                setHolidayForm((f) => ({ ...f, holidayDate: e.target.value }))
              }
              required
            />
            <input
              className="w-full border rounded-lg px-3 py-2"
              type="number"
              placeholder="Uplift %"
              value={holidayForm.upliftPercent}
              onChange={(e) =>
                setHolidayForm((f) => ({
                  ...f,
                  upliftPercent: Number(e.target.value),
                }))
              }
            />
            <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg">
              {editingHolidayId ? "Update" : "Add"} holiday
            </button>
          </form>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Uplift %</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {holidayList.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-4 py-2">{row.name}</td>
                    <td className="px-4 py-2">{row.holidayDate}</td>
                    <td className="px-4 py-2">{row.upliftPercent}%</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <div className="flex items-center justify-end gap-3">
                        <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => {
                          setEditingHolidayId(row.id);
                          setHolidayForm({
                            name: row.name,
                            holidayDate: row.holidayDate,
                            upliftPercent: Number(row.upliftPercent),
                            status: row.status,
                            remarks: row.remarks ?? "",
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-600"
                        onClick={async () => {
                          await axiosInstance.delete(`/public-holidays/${row.id}`);
                          await loadHolidays();
                        }}
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === "via" && (
        <form
          className="mt-6 max-w-lg bg-white rounded-lg shadow p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            saveVia();
          }}
        >
          <p className="text-sm font-semibold text-gray-800">Via stop charges</p>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Charge per via (£)"
            value={viaSettings.chargePerVia}
            onChange={(e) => setViaSettings((s) => ({ ...s, chargePerVia: e.target.value }))}
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Free vias included"
            value={viaSettings.freeVias}
            onChange={(e) => setViaSettings((s) => ({ ...s, freeVias: e.target.value }))}
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Maximum vias (optional)"
            value={viaSettings.maxVias}
            onChange={(e) => setViaSettings((s) => ({ ...s, maxVias: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={viaSettings.useLocationCharges}
              onChange={(e) =>
                setViaSettings((s) => ({
                  ...s,
                  useLocationCharges: e.target.checked,
                }))
              }
            />
            Also apply location charges on via postcodes
          </label>

          <div className="border-t border-gray-200 pt-4 space-y-4">
            <p className="text-sm font-semibold text-gray-800">Wait during journey (at via stops)</p>
            <p className="text-sm text-gray-600">
              Charged when a customer selects &quot;Wait during journey&quot; on a via stop in the
              booking form.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={viaSettings.waitPricingEnabled}
                onChange={(e) =>
                  setViaSettings((s) => ({
                    ...s,
                    waitPricingEnabled: e.target.checked,
                  }))
                }
              />
              Enable wait-at-via pricing
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Flat charge per wait stop (£)"
              value={viaSettings.chargePerWaitStop}
              onChange={(e) =>
                setViaSettings((s) => ({ ...s, chargePerWaitStop: e.target.value }))
              }
            />
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="% of fare per wait stop"
              value={viaSettings.percentOfFarePerWaitStop}
              onChange={(e) =>
                setViaSettings((s) => ({ ...s, percentOfFarePerWaitStop: e.target.value }))
              }
            />
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Minimum charge per wait stop (£)"
              value={viaSettings.minimumWaitChargePerStop}
              onChange={(e) =>
                setViaSettings((s) => ({ ...s, minimumWaitChargePerStop: e.target.value }))
              }
            />
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Free waiting minutes per stop"
              value={viaSettings.freeWaitMinutesPerStop}
              onChange={(e) =>
                setViaSettings((s) => ({ ...s, freeWaitMinutesPerStop: e.target.value }))
              }
            />
          </div>

          <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg">
            Save
          </button>
        </form>
      )}

      {!loading && activeTab === "cash" && (
        <form
          className="mt-6 max-w-lg bg-white rounded-lg shadow p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            saveCash();
          }}
        >
          <p className="text-sm text-gray-500">
            Cash is only offered on the booking site when the pickup is within this radius of base.
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cashSettings.enabled}
              onChange={(e) =>
                setCashSettings((s) => ({ ...s, enabled: e.target.checked }))
              }
            />
            Enable cash payments (within radius)
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Base latitude"
            value={cashSettings.baseLatitude}
            onChange={(e) =>
              setCashSettings((s) => ({ ...s, baseLatitude: e.target.value }))
            }
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Base longitude"
            value={cashSettings.baseLongitude}
            onChange={(e) =>
              setCashSettings((s) => ({ ...s, baseLongitude: e.target.value }))
            }
          />
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder="Radius (miles)"
            value={cashSettings.radiusMiles}
            onChange={(e) =>
              setCashSettings((s) => ({ ...s, radiusMiles: e.target.value }))
            }
          />
          <button type="submit" className="px-4 py-2 bg-brand-500 text-white rounded-lg">
            Save
          </button>
        </form>
      )}
    </div>
  );
}
